import { useTranslations } from 'next-intl'
import React, { useMemo } from 'react'

import DashboardLoadingIcon from '@/components/icon/dashboard-loading-icon'
import { Skeleton } from '@/components/ui/skeleton'
import { WebPushNotificationSubscriptionDevice } from '@/components/web-push-notification/web-push-notification-subscription-device'

import { useInfiniteWebPushNotificationSubscriptionsQuery } from '@/hooks/query/notification-subscription'
import { useInfiniteScrollFetch } from '@/hooks/use-infinite-scroll-fetch'
import { cn } from '@/lib/utils'

const WebPushNotificationSubscriptionList = ({ className }: { className?: string }) => {
  const tComponent = useTranslations('Notification.components.web_push_notification_subscription_list')
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useInfiniteWebPushNotificationSubscriptionsQuery()

  const setObserverRef = useInfiniteScrollFetch({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  })

  const hasNoData = useMemo(() => {
    return !data || data.pages.length === 0 || data.pages[0]?.items.length === 0
  }, [data])

  const shouldShowSkeletonLoading = useMemo(() => {
    return isFetching && hasNoData
  }, [isFetching, hasNoData])

  return (
    <div className={cn('flex min-h-[180px] flex-[1_1_0] flex-col gap-4 overflow-y-auto', className)}>
      {shouldShowSkeletonLoading ? (
        <div className="flex animate-fade-in flex-col gap-4">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : hasNoData ? (
        <p className="text-center text-sm font-normal text-muted-foreground">{tComponent('messages.no_data')}</p>
      ) : (
        <>
          {data?.pages?.map((group, i) => {
            return (
              <React.Fragment key={i}>
                {group.items.map((item) => {
                  return (
                    <WebPushNotificationSubscriptionDevice
                      key={item._id}
                      subscriptionId={item._id}
                      userAgent={item.userAgent}
                      className="animate-fade-in"
                    />
                  )
                })}
              </React.Fragment>
            )
          })}

          {isFetchingNextPage && (
            <div className="flex items-center justify-center py-6">
              <DashboardLoadingIcon />
            </div>
          )}
          <div ref={setObserverRef} className="h-8 w-full" aria-label="Loading sentinel" aria-hidden="true" />
        </>
      )}
    </div>
  )
}
WebPushNotificationSubscriptionList.displayName = 'WebPushNotificationSubscriptionList'

export { WebPushNotificationSubscriptionList }
